/**
 * <MentionTextarea> — a textarea (or input) that pops a roster picker
 * when the user types `@`, captures the selected member IDs so the
 * server can fan out notifications, and renders the result back as
 * `@Name ` in the text.
 *
 * Used by chat / prayer / photo caption / photo comment.
 */
import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { FAMILY_MENTION_ID } from '@ts/schema/types';
import { useAuth } from '../lib/auth';

export interface MentionInputProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'onChange' | 'value'
> {
  value: string;
  onChange: (value: string, mentions: string[]) => void;
  /** Member ids currently referenced in `value`. Pass through if you
   *  control state externally; otherwise the component recomputes
   *  from the text. */
  mentions?: string[];
  /** Disable the popover (still accepts the `@` character as plain text). */
  asInput?: boolean;
  /** Optional override for the @-picker roster. When omitted, defaults
   *  to the signed-in member's household roster from useAuth(). Set
   *  this on Generations / circle-context surfaces to include cross-
   *  household members (household ∪ circle, deduped, self filtered).
   *  Family-tab callers leave this unset and stay household-only. */
  members?: RosterEntry[];
}

export interface RosterEntry {
  id: string;
  displayName: string;
  /** Optional household label shown after the name in the picker.
   *  Set only on cross-household entries (Generations circle picker);
   *  left undefined for own-household members so the picker doesn't
   *  get noisy when there's no ambiguity. */
  familyName?: string;
  /** Optional secondary hint shown after the name (e.g. the `@myfamily`
   *  entry explaining it pings the whole family). Display-only — does
   *  not affect collision disambiguation. */
  hint?: string;
}

/** Resolved by scanning the text for `@Name` tokens and matching against
 *  the picked-id set. We use the picker selections as authoritative; this
 *  recompute is for callers that don't track mentions themselves. */
function deriveMentions(text: string, picked: Map<string, string>): string[] {
  const ids = new Set<string>();
  for (const [name, id] of picked) {
    const re = new RegExp(`(^|\\s)@${escapeRe(name)}(?=\\s|$)`, 'i');
    if (re.test(text)) ids.add(id);
  }
  return [...ids];
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export const MentionTextarea = forwardRef<HTMLTextAreaElement, MentionInputProps>(
  function MentionTextarea(
    { value, onChange, mentions: _mentions, members, ...rest },
    ref,
  ) {
    const auth = useAuth();
    const me = auth.state === 'signed-in' ? auth.me : null;
    const taRef = useRef<HTMLTextAreaElement | null>(null);
    useImperativeHandle(ref, () => taRef.current!);

    // Roster: caller-supplied (Generations / circle contexts) or the
    // household members from useAuth() (family-tab default). Self is
    // always filtered out either way.
    const roster: RosterEntry[] = useMemo(() => {
      const meId = me?.member.id;
      if (members) {
        // Circle / cross-household context: keep the roster to explicit
        // members. `@myfamily` is a household-only concept, so we don't
        // surface it here to avoid ambiguity about which family.
        return members.filter((m) => m.id !== meId);
      }
      if (!me) return [];
      const others = me.members
        .filter((m) => m.id !== me.member.id)
        .map((m) => ({ id: m.id, displayName: m.displayName }));
      // Family-wide sentinel, only meaningful when there's someone else
      // to notify. Listed first so it's the default highlight.
      if (!others.length) return others;
      const family: RosterEntry = {
        id: FAMILY_MENTION_ID,
        displayName: 'myfamily',
        hint: 'Everyone in your family',
      };
      return [family, ...others];
    }, [members, me]);

    /** Member-name → id mapping of EVERY member the user has ever picked
     *  in this textarea session. Lets us recompute the mentions array on
     *  every keystroke without re-running the picker. */
    const pickedRef = useRef<Map<string, string>>(new Map());
    // Seed from roster — picker selections will overwrite.
    for (const m of roster) pickedRef.current.set(m.displayName, m.id);

    /** Whether the popover is open and what the user has typed since `@`. */
    const [trigger, setTrigger] = useState<{
      caret: number;
      atIndex: number;
      query: string;
    } | null>(null);
    const [highlight, setHighlight] = useState(0);

    function emit(nextText: string) {
      onChange(nextText, deriveMentions(nextText, pickedRef.current));
    }

    function handleChange(e: FormEvent<HTMLTextAreaElement>) {
      const next = e.currentTarget.value;
      const caret = e.currentTarget.selectionStart ?? next.length;

      // Look back from the caret to find an unbroken `@<chars>` run.
      const before = next.slice(0, caret);
      const m = before.match(/(^|\s)@([\w\-']*)$/);
      if (m) {
        setTrigger({
          caret,
          atIndex: before.length - m[2]!.length - 1,
          query: m[2]!,
        });
        setHighlight(0);
      } else {
        setTrigger(null);
      }
      emit(next);
    }

    const filtered = useMemo(() => {
      if (!trigger) return roster;
      const q = trigger.query.toLowerCase();
      return roster.filter((m) => m.displayName.toLowerCase().startsWith(q));
    }, [trigger, roster]);

    function pickMember(m: RosterEntry) {
      if (!trigger || !taRef.current) return;
      // Collision: if the roster has multiple entries that share this
      // display name (two "Dad"s across households in a circle picker),
      // disambiguate the inserted text + storage key with the family
      // suffix. pickedRef is keyed by label, so a non-unique
      // displayName would otherwise overwrite the previous selection
      // and route every "@Dad" to a single id at send time.
      const collidesInRoster =
        roster.filter(
          (r) =>
            r.id !== m.id && r.displayName.toLowerCase() === m.displayName.toLowerCase(),
        ).length > 0;
      const label =
        collidesInRoster && m.familyName
          ? `${m.displayName} (${m.familyName})`
          : m.displayName;
      pickedRef.current.set(label, m.id);
      const before = value.slice(0, trigger.atIndex);
      const after = value.slice(trigger.caret);
      const insert = `@${label} `;
      const next = before + insert + after;
      const newCaret = (before + insert).length;
      setTrigger(null);
      emit(next);
      // Restore caret after React commits.
      queueMicrotask(() => {
        const el = taRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(newCaret, newCaret);
      });
    }

    function handleKey(e: KeyboardEvent<HTMLTextAreaElement>) {
      if (trigger && filtered.length) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setHighlight((h) => (h + 1) % filtered.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setHighlight((h) => (h - 1 + filtered.length) % filtered.length);
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          pickMember(filtered[highlight]!);
          return;
        }
        if (e.key === 'Escape') {
          setTrigger(null);
          return;
        }
      }
      rest.onKeyDown?.(e);
    }

    return (
      <div className="relative">
        <textarea
          {...rest}
          ref={taRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKey}
        />
        {trigger && filtered.length > 0 && (
          <ul
            role="listbox"
            className="absolute z-30 bottom-full mb-1 left-0 right-0 max-w-xs bg-paper border border-line rounded-xl shadow-lg overflow-hidden"
          >
            {filtered.map((m, i) => (
              <li
                key={m.id}
                role="option"
                aria-selected={i === highlight}
                className={`px-3 py-2 cursor-pointer ${
                  i === highlight ? 'bg-line/60' : ''
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pickMember(m);
                }}
              >
                <span className="font-medium text-ink">@{m.displayName}</span>
                {m.familyName && (
                  <span className="ml-2 text-xs text-soft">· {m.familyName}</span>
                )}
                {m.hint && <span className="ml-2 text-xs text-soft">· {m.hint}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  },
);

/** Render text with @Name spans visually highlighted. */
export function renderWithMentions(
  text: string,
  opts?: { inverted?: boolean },
): ReactNode {
  // Single-token names + an optional " (Family Name)" disambiguation
  // suffix that the picker appends on display-name collisions. The
  // suffix lets two cross-household "Dad"s remain distinct in the
  // text body. We tolerate any non-`)` characters inside the parens
  // so family names with spaces / ampersands render together.
  // Previously `(?:\s[\w'\-]+)?` greedy-swallowed the next real word.
  const parts = text.split(/(@[\w'\-]+(?:\s\([^)]+\))?)/g);
  const cls = opts?.inverted
    ? 'font-semibold underline underline-offset-2'
    : 'text-oxblood font-medium';
  return parts.map((p, i) =>
    p.startsWith('@') ? (
      <span key={i} className={cls}>
        {p}
      </span>
    ) : (
      p
    ),
  );
}
